import { EqlFieldsAutoComplete } from '@modules/common/components/EqlFieldsAutoComplete';
import { EqlFieldName } from '@modules/common/constants';
import { useFetchEQLFields } from '@modules/common/hooks';
import { DEFAULT_QUANTIFY_SORT_BY } from '@modules/quantify/constants';
import {
  useFetchMeasuredAsCriteria,
  useFetchQuantifyReport,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyState } from '@modules/quantify/store/types';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { AutocompleteRenderInputParams, TextField } from '@mui/material';
import {
  EqlField,
  EqlFieldValueType,
  ObjectType,
  QuantifyVisualisationType,
} from 'generated/graphql';
import { SyntheticEvent, useEffect, useState } from 'react';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

export function GroupByField() {
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);

  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.formulaQuery?.criteria,
  );
  const isTableQueryInProgress = useQuantifyStore(
    ({ visual }) => visual.isTableQueryInProgress,
  );
  const shouldShowCategories = useQuantifyStore(
    ({ visual }) => visual.shouldShowCategories,
  );
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const trackQuantifyQueryEvent = useTriggerQuantifyQueryAnalyticsEvent();

  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);
  const { criteriaList } = useFetchMeasuredAsCriteria({
    field: groupBy,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (shouldFetchReportData) {
      setShouldFetchReportData(false);
      fetchReportData();
      trackQuantifyQueryEvent('Quantify - Query Executed');
    }
  }, [fetchReportData, shouldFetchReportData, trackQuantifyQueryEvent]);

  const { fields, isLoading } = useFetchEQLFields({
    shouldExcludeNonGroupByFields: true,
    shouldExcludeNonGroupableMetadataFields: true,
    shouldIncludeFeedbackRecordsFilter: true,
    fetchPolicy: 'cache-and-network',
  });

  function onGroupByChange(_: SyntheticEvent, eqlField: EqlField | null) {
    if (!eqlField) {
      return;
    }

    const getUpdatedMeasuredAsCriteria = () => {
      if (measuredAsCriteria?.valueType === EqlFieldValueType.None) {
        return measuredAsCriteria;
      }

      return criteriaList.find(
        (criteria) =>
          criteria.objectType === ObjectType.Feedback &&
          criteria.valueType === EqlFieldValueType.None,
      );
    };

    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        groupByField: eqlField,
        formulaQuery: {
          eqlFunction: EQLFunctionName.Count,
          criteria: getUpdatedMeasuredAsCriteria(),
        },
        shouldShowAnomalies: false,
        sortBy: DEFAULT_QUANTIFY_SORT_BY,
      },
    });

    const queryPayload: Partial<QuantifyState['query']> = {
      visibleDataPoints: [],
      shouldShowTopDataPoints: true,
      visualisationType: QuantifyVisualisationType.Bar,
    };

    const visualPayload: Partial<QuantifyState['visual']> = {
      shouldShowCategories: shouldShowCategories,
    };

    if (eqlField?.field !== EqlFieldName.Entity && shouldShowCategories) {
      visualPayload.shouldShowCategories = false;
    }

    dispatch({
      type: QuantifyActionType.SET_VISUAL,
      payload: visualPayload,
    });

    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: queryPayload,
    });

    setShouldFetchReportData(true);

    trackQuantifyQueryEvent('Quantify - Group by Selected', {
      showMeLabel: eqlField.displayName,
    });
  }

  return (
    <EqlFieldsAutoComplete
      id="quantify-group-by-field"
      value={groupBy}
      options={fields}
      onChange={onGroupByChange}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          placeholder="What would you like to plot?"
          autoFocus
        />
      )}
      popupIcon={<ArrowDropDown />}
      disabled={isTableQueryInProgress}
      loading={isLoading}
    />
  );
}
